import {
  filterAirportSuggestOptions,
  mapSearchCityAirportResponse,
  groupAirportSuggestOptions,
  normalizeAirportSuggestChildMarkers,
} from '../map/mapSearchCityAirportResponse';

/**
 * In-memory airport suggest cache shared across mounts.
 * Host injects HTTP only. Filtering, grouping, and child-arrow
 * rules live in this package.
 */

/** Bump when mapped option shape / hierarchy rules change. */
const CACHE_VERSION = 3;

let cachedOptions = null;
let inflight = null;
let loadedAt = null;
let cacheVersion = null;

function hierarchyLooksStale(options) {
  if (!Array.isArray(options) || !options.length) return true;
  // Children should be marked isChild when otherAirportCodes points at a metro.
  return options.some((option) => {
    const other = option?.value?.otherAirportCodes;
    const isCity = String(option?.value?.type || '').toLowerCase() === 'city';
    return (
      Array.isArray(other) &&
      other.length > 0 &&
      !isCity &&
      option.isChild !== true
    );
  });
}

export function getCachedAirportSuggestOptions() {
  return cachedOptions;
}

export function isAirportSuggestCacheReady() {
  return Array.isArray(cachedOptions) && cachedOptions.length > 0;
}

export function getAirportSuggestCacheLoadedAt() {
  return loadedAt;
}

export function clearAirportSuggestCache() {
  cachedOptions = null;
  loadedAt = null;
  inflight = null;
  cacheVersion = null;
}

/**
 * @param {() => Promise<any[]>} fetchAllMapped
 * @returns {Promise<any[]>}
 */
export function ensureAirportSuggestOptions(fetchAllMapped) {
  if (cacheVersion !== CACHE_VERSION || hierarchyLooksStale(cachedOptions)) {
    cachedOptions = null;
    loadedAt = null;
    // keep inflight if a fetch is already in progress for this version
    if (cacheVersion !== CACHE_VERSION) {
      inflight = null;
    }
  }

  if (Array.isArray(cachedOptions) && cachedOptions.length > 0) {
    return Promise.resolve(cachedOptions);
  }
  if (inflight) {
    return inflight;
  }
  if (typeof fetchAllMapped !== 'function') {
    return Promise.reject(new Error('fetchAllMapped is required'));
  }

  inflight = Promise.resolve()
    .then(() => fetchAllMapped())
    .then((list) => {
      let next = Array.isArray(list) ? list : [];
      // If host somehow returned raw rows, normalize.
      if (next.length && next[0] && next[0].value == null && next[0].code) {
        next = mapSearchCityAirportResponse(next);
      }
      cachedOptions = next;
      loadedAt = Date.now();
      cacheVersion = CACHE_VERSION;
      inflight = null;
      return cachedOptions;
    })
    .catch((err) => {
      inflight = null;
      throw err;
    });

  return inflight;
}

/**
 * Sync filter against cache. Returns null if cache is not ready
 * (caller should fall back to remote suggest).
 *
 * @param {string} query
 * @param {{ maxResults?: number }} [opts]
 * @returns {any[] | null}
 */
export function suggestAirportsFromCache(query, opts = {}) {
  if (!isAirportSuggestCacheReady() || hierarchyLooksStale(cachedOptions)) {
    return null;
  }
  return filterAirportSuggestOptions(cachedOptions, query, opts);
}

/**
 * Group + child-arrow rules for already-mapped options.
 * Used when serving a remote v2 payload through the same pipeline.
 */
export function finalizeAirportSuggestOptions(mapped) {
  const rows = Array.isArray(mapped) ? mapped : [];
  const all = getCachedAirportSuggestOptions();
  if (all?.length) {
    return normalizeAirportSuggestChildMarkers(
      groupAirportSuggestOptions(rows, all)
    );
  }
  return normalizeAirportSuggestChildMarkers(rows);
}

/**
 * Typed-suggest entry point. Hosts inject HTTP; this package owns
 * cache lookup, v2 fallback mapping, metro grouping, and arrows.
 *
 * @param {string} query
 * @param {{
 *   fetchAllMapped?: () => Promise<any[]>,
 *   fetchRemote?: (query: string) => Promise<any>,
 *   maxResults?: number,
 * }} [io]
 * @returns {Promise<any[]>}
 */
export async function searchAirportSuggest(query, io = {}) {
  const local = suggestAirportsFromCache(query, { maxResults: io.maxResults });
  if (local) {
    return local;
  }

  if (typeof io.fetchAllMapped === 'function') {
    ensureAirportSuggestOptions(io.fetchAllMapped).catch(() => {});
  }

  if (typeof io.fetchRemote !== 'function') {
    return [];
  }

  const remote = await io.fetchRemote(query);
  return finalizeAirportSuggestOptions(mapSearchCityAirportResponse(remote));
}
